/**
 * The board's live channel: pointers, who is present, and word that the
 * document moved.
 *
 * Not a write path. Operations still go over HTTP, through the one applier
 * that validates them; this socket says where each person is pointing, who is
 * on the board, and that it changed — so the other side fetches at once
 * instead of up to a poll later.
 *
 * Opening it costs a ticket, fetched over the same authenticated (or
 * token-scoped) call everything else uses. A browser cannot put an
 * Authorization header on a WebSocket, and trusting whoever knows the map id
 * would not be authorisation.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How often a pointer position goes out.
 *
 * Twenty-five a second, eased over one sample on the way in: fewer than that
 * and the glide is long enough to feel like lag; many more and it is traffic
 * nobody can see.
 */
const CURSOR_INTERVAL_MS = 40;
/** How long before a reconnection is attempted, and how far it backs off. */
const RECONNECT_MS = 1500;
const MAX_RECONNECT_MS = 15000;

export interface RemoteCursor {
    id: string;
    kind: "therapist" | "participant";
    name: string;
    x: number;
    y: number;
}

export interface BoardMember {
    id: string;
    kind: "therapist" | "participant";
    name: string;
}

export interface BoardPresence {
    /** The therapist ended this session's access. */
    revoked: boolean;
    /** Everyone else's pointer, keyed by who they are. */
    cursors: RemoteCursor[];
    members: BoardMember[];
    /** True while the channel is carrying traffic. */
    live: boolean;
    /** Say where this pointer is. Throttled; safe to call per mouse move. */
    reportCursor: (x: number, y: number) => void;
    /** Tell the others the document moved, after a write of our own landed. */
    reportChange: (version: number) => void;
}

type Incoming =
    | { type: "welcome"; you: BoardMember; members: BoardMember[]; cursors: RemoteCursor[] }
    | { type: "presence"; members: BoardMember[] }
    | { type: "cursor"; id: string; kind: RemoteCursor["kind"]; name: string; x: number; y: number }
    | { type: "board-changed"; version: number | null; by: BoardMember }
    | { type: "revoked" };

const socketUrl = (ticket: string): string => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    return `${protocol}://${window.location.host}/api/ws/systemic-maps/${ticket}`;
};

export function useBoardPresence(
    /** Fetches a fresh ticket. Null while there is nothing to connect to. */
    requestTicket: (() => Promise<string>) | null,
    /** Called when someone else changed the board. */
    onBoardChanged: () => void,
): BoardPresence {
    const [cursors, setCursors] = useState<RemoteCursor[]>([]);
    const [members, setMembers] = useState<BoardMember[]>([]);
    const [live, setLive] = useState(false);
    /** True once the therapist took the link back. Never goes back to false. */
    const [revoked, setRevoked] = useState(false);

    const socket = useRef<WebSocket | null>(null);
    const lastSent = useRef(0);
    const pending = useRef<{ x: number; y: number } | null>(null);
    const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Held in refs so reconnecting does not depend on a caller keeping its
    // callbacks referentially stable.
    const changedRef = useRef(onBoardChanged);
    changedRef.current = onBoardChanged;
    const ticketRef = useRef(requestTicket);
    ticketRef.current = requestTicket;

    useEffect(() => {
        if (!requestTicket) return;

        let cancelled = false;
        let retry = RECONNECT_MS;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;

        const connect = async () => {
            if (cancelled) return;
            try {
                const ticket = await ticketRef.current!();
                if (cancelled) return;

                const ws = new WebSocket(socketUrl(ticket));
                socket.current = ws;

                ws.onopen = () => {
                    if (cancelled) return;
                    setLive(true);
                    retry = RECONNECT_MS;
                };

                ws.onmessage = (event) => {
                    let message: Incoming;
                    try {
                        message = JSON.parse(event.data as string) as Incoming;
                    } catch {
                        // A malformed frame is not worth taking the board down.
                        return;
                    }

                    if (message.type === "welcome") {
                        setMembers(message.members);
                        setCursors(message.cursors.filter((c) => c.id !== message.you.id));
                        return;
                    }
                    if (message.type === "presence") {
                        setMembers(message.members);
                        // Someone left: their pointer goes with them, or it
                        // hangs on the board as a person who is not there.
                        const here = new Set(message.members.map((m) => m.id));
                        setCursors((current) => current.filter((c) => here.has(c.id)));
                        return;
                    }
                    if (message.type === "revoked") {
                        // The therapist took the link back. Said outright,
                        // because a board that merely stops answering reads
                        // as a dropped connection — and this one is not
                        // coming back.
                        setRevoked(true);
                        cancelled = true;
                        ws.close();
                        return;
                    }
                    if (message.type === "cursor") {
                        setCursors((current) => [
                            ...current.filter((c) => c.id !== message.id),
                            message,
                        ]);
                        return;
                    }
                    changedRef.current();
                };

                ws.onclose = () => {
                    setLive(false);
                    socket.current = null;
                    if (cancelled) return;
                    // The board still works over HTTP while this is down, so a
                    // failed reconnection degrades rather than breaks.
                    retryTimer = setTimeout(connect, retry);
                    retry = Math.min(retry * 2, MAX_RECONNECT_MS);
                };

                ws.onerror = () => ws.close();
            } catch {
                if (cancelled) return;
                retryTimer = setTimeout(connect, retry);
                retry = Math.min(retry * 2, MAX_RECONNECT_MS);
            }
        };

        void connect();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (flushTimer.current) clearTimeout(flushTimer.current);
            socket.current?.close();
            socket.current = null;
            setLive(false);
            setCursors([]);
            setMembers([]);
        };
        // Re-connects when there is a different board to connect to, not on
        // every render of whatever holds this hook.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Boolean(requestTicket)]);

    const send = useCallback((message: Record<string, unknown>) => {
        const ws = socket.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try {
            ws.send(JSON.stringify(message));
        } catch {
            /* the close handler will reconnect */
        }
    }, []);

    /**
     * A pointer moves sixty times a second and nobody can see that many.
     * Positions are coalesced to one every `CURSOR_INTERVAL_MS`, with the last
     * one always sent — a cursor that stops one frame short of where the
     * person actually is looks broken.
     */
    const reportCursor = useCallback(
        (x: number, y: number) => {
            pending.current = { x, y };
            const now = Date.now();
            const wait = Math.max(0, CURSOR_INTERVAL_MS - (now - lastSent.current));

            if (flushTimer.current) return;
            flushTimer.current = setTimeout(() => {
                flushTimer.current = null;
                const at = pending.current;
                pending.current = null;
                if (!at) return;
                lastSent.current = Date.now();
                send({ type: "cursor", ...at });
            }, wait);
        },
        [send],
    );

    const reportChange = useCallback(
        (version: number) => send({ type: "changed", version }),
        [send],
    );

    return { cursors, members, live, revoked, reportCursor, reportChange };
}
